import type { TravelPreferences } from '@/types/profile'

/**
 * VOY-354: the unified Style + Interests pill cloud. This list replaces
 * the legacy `TRAVEL_STYLES` (10 slugs) + `INTERESTS` (16 human labels)
 * that used to live in two separate UI sections.
 *
 * Order is authoritative — the read-mode grid and edit-mode ToggleGroup
 * both render in this sequence.
 */
export const STYLE_INTEREST_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'adventure', label: 'Adventure' },
  { value: 'relaxation', label: 'Relaxation' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'foodie', label: 'Foodie' },
  { value: 'nature', label: 'Nature' },
  { value: 'urban', label: 'Urban' },
  { value: 'beach', label: 'Beach' },
  { value: 'mountains', label: 'Mountains' },
  { value: 'photography', label: 'Photography' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'nightlife', label: 'Nightlife' },
  { value: 'history', label: 'History' },
  { value: 'wellness', label: 'Wellness' },
  { value: 'sports', label: 'Sports' },
  { value: 'music', label: 'Music' },
  { value: 'wildlife', label: 'Wildlife' },
]

const VALID_SLUGS: ReadonlySet<string> = new Set(STYLE_INTEREST_OPTIONS.map((o) => o.value))

/**
 * Map legacy `INTERESTS` human labels (from the old preferences-tab.tsx
 * INTERESTS constant) to unified slugs. Entries with no mapping are
 * dropped silently during backfill — the user re-picks from the new cloud.
 */
const LEGACY_INTEREST_LABEL_TO_SLUG: Record<string, string> = {
  'local cuisine': 'foodie',
  'music & festivals': 'music',
  'wellness & spa': 'wellness',
  'wildlife': 'wildlife',
  'history': 'history',
  'sports & fitness': 'sports',
  'nightlife': 'nightlife',
  'architecture': 'cultural',
  'hiking & trekking': 'mountains',
  'water sports': 'beach',
  'family activities': 'cultural',
  'luxury experiences': 'relaxation',
  'budget adventures': 'adventure',
  'off-the-beaten-path': 'adventure',
  // 'art & museums' and 'wine & breweries' have no close match → dropped.
}

function normalizeLegacyInterest(raw: string): string | null {
  const slug = raw.trim().toLowerCase()
  if (VALID_SLUGS.has(slug)) return slug
  return LEGACY_INTEREST_LABEL_TO_SLUG[slug] ?? null
}

/**
 * Read the unified `style_interests` selection, falling back to the legacy
 * `travel_style[]` + `interests[]` arrays when the new field is empty.
 * Returns only slugs that exist in STYLE_INTEREST_OPTIONS — legacy values
 * with no clean mapping are silently dropped.
 */
export function readStyleInterests(prefs: TravelPreferences | null | undefined): string[] {
  if (!prefs) return []

  if (Array.isArray(prefs.style_interests) && prefs.style_interests.length > 0) {
    return prefs.style_interests.filter((slug) => VALID_SLUGS.has(slug))
  }

  // Backfill from legacy arrays.
  const legacy = new Set<string>()
  for (const s of prefs.travel_style ?? []) {
    const slug = s.toString().trim().toLowerCase()
    if (VALID_SLUGS.has(slug)) legacy.add(slug)
  }
  for (const i of prefs.interests ?? []) {
    const normalized = normalizeLegacyInterest(String(i))
    if (normalized) legacy.add(normalized)
  }
  return Array.from(legacy)
}

/**
 * Humanize a slug for summary display (e.g. "mid-range" → "Mid-range").
 * Used by the Travel preferences card read-mode rows.
 */
export function humanize(value: string | null | undefined): string {
  if (!value) return ''
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-')
}

/**
 * Shared option lists for the single-select preference rows.
 */
export const BUDGET_OPTIONS = [
  { value: 'economy', label: 'Economy' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'premium', label: 'Premium' },
  { value: 'luxury', label: 'Luxury' },
] as const

export const PACE_OPTIONS = [
  { value: 'slow', label: 'Slow' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'fast', label: 'Fast' },
] as const

export const ACCOMMODATION_OPTIONS = [
  { value: 'budget', label: 'Budget' },
  { value: 'mid-range', label: 'Mid-range' },
  { value: 'luxury', label: 'Luxury' },
  { value: 'unique', label: 'Unique' },
] as const

export const GROUP_OPTIONS = [
  { value: 'solo', label: 'Solo' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'friends', label: 'Friends' },
  { value: 'business', label: 'Business' },
] as const

export const PLANNING_OPTIONS = [
  { value: 'spontaneous', label: 'Spontaneous' },
  { value: 'planned', label: 'Planned' },
  { value: 'mixed', label: 'Mixed' },
] as const
